import type {Event} from '@sentry/types'

/** 仅用于 Sentry 上报脱敏，不影响业务 API 请求体 */
const SENSITIVE_KEYS = new Set([
  'token',
  'h_did',
  'deviceinfo',
  'nickname',
  'password',
  'secret',
  'authorization',
  'cookie',
  'websign',
  'auth',
  'username',
  'userstatus',
  'android_id',
])

function stripUrlQuery(value: string): string {
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://example.com'
    const url = new URL(value, base)
    return `${url.origin}${url.pathname}`
  } catch {
    return value.split('?')[0]?.split('#')[0] || value
  }
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(key.toLowerCase())
}

function scrubDeep(value: unknown, depth: number): unknown {
  if (value == null || depth > 12) {
    return value
  }

  if (typeof value === 'string') {
    if (
      value.includes('deviceinfo=') ||
      value.includes('token=') ||
      /^https?:\/\//i.test(value)
    ) {
      return stripUrlQuery(value)
    }
    return value
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubDeep(item, depth + 1))
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {}
    Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
      if (isSensitiveKey(key)) {
        return
      }
      result[key] = scrubDeep(val, depth + 1)
    })
    return result
  }

  return value
}

function scrubRequest(request: NonNullable<Event['request']>): void {
  if (typeof request.url === 'string') {
    request.url = stripUrlQuery(request.url)
  }

  if (request.headers && typeof request.headers === 'object') {
    const headers = {...request.headers}
    delete headers.Referer
    delete headers.referer
    delete headers.Cookie
    delete headers.cookie
    request.headers = headers
  }

  if ('query_string' in request) {
    delete request.query_string
  }

  if (request.data) {
    request.data = scrubDeep(request.data, 0) as typeof request.data
  }
}

/**
 * 在 beforeSend / beforeSendTransaction 中调用，仅清理发往 Sentry 的 payload。
 */
export function sanitizeSentryPayload<T extends Event>(event: T): T {
  if (event.request) {
    scrubRequest(event.request)
  }

  if (event.breadcrumbs?.length) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) =>
      scrubDeep(breadcrumb, 0)
    ) as T['breadcrumbs']
  }

  if (event.extra) {
    event.extra = scrubDeep(event.extra, 0) as T['extra']
  }

  if (event.contexts) {
    event.contexts = scrubDeep(event.contexts, 0) as T['contexts']
  }

  if (event.tags) {
    event.tags = scrubDeep(event.tags, 0) as T['tags']
  }

  if (event.user) {
    event.user = scrubDeep(event.user, 0) as T['user']
  }

  return event
}
